import $ from 'jquery'
import Cookies from 'js-cookie'
import cloneDeep from 'lodash/cloneDeep'
import lget from 'lodash/get'
import has from 'lodash/has'
import includes from 'lodash/includes'
import isArray from 'lodash/isArray'
import isBoolean from 'lodash/isBoolean'
import isNil from 'lodash/isNil'
import remove from 'lodash/remove'
import lset from 'lodash/set'
import lunset from 'lodash/unset'

import { setConfig } from 'api/actions'
import AddDefaultExcludes from 'api/AddDefaultExcludes'
import PoziAPIData from 'api/data'
import Migrate from 'api/migrate/Migrate'
import * as PoziUtils from 'api/utils'
import { getSiteConfigNew } from 'config/adaptor/internalApi'
import { configManagerOpen } from 'ConfigManager/actions'
import { whatsHereLayerConfig } from 'drawer/tabs/info/WhatsHereUtils'
import { currentTargetLayerConfig } from 'utils/target'
import { parseHash } from 'utils/url'

const domainName = import.meta.env.VITE_DOMAIN_NAME
const configLocation = import.meta.env.VITE_CONFIG_LOCATION

export const ANNOTATION_LAYER_TITLEID = 'poziannotation'
export const SITES_MIGRATED_TO_CONFIG_MANAGER = ['staging', 'frankston']
export const DEFAULT_ID_WHEN_UNSET = '$id'

/**
 * Global model object.
 * ALWAYS access via Model Adapter functions.
 * NEVER reference, read or update this object directly outside of this module.
 */
const __GModelData = {} // ALWAYS access via Model Adapter functions.
const __GModelObservers = [] // ALWAYS access via Model Adapter functions.

/**
 * Manages the model.
 * Loads the siteconfig.
 * Contains accessor functions to read and manipulate the model.
 * May contain persistent functions in future, e.g. Read/Write from localstorage, etc.
 */

/**
 * Sets the url parameters into the model
 *
 */
export const initialise = async function () {
  if (__GModelData.initialised) {
    console.log('Model already initialised with site', __GModelData.sitename)
    return
  }

  const appInfo = window.getAppInfo()
  const { environment, localhost, map, mode } = appInfo
  let { site } = appInfo

  // If no sitename is specified, try reading the site name from a cookie. If that fails, we will fall back to 'default'
  if (!site && environment !== 'development') {
    const siteCookie = Cookies.get('pozi-site')
    console.debug('Model - initialise - read site name from cookie', siteCookie)

    site = siteCookie || 'default'

    // Store site in state so it gets reflected in the URL
    setState('site', null, site)
  } else {
    Cookies.set('pozi-site', site, {
      domain: `.${domainName}`, // available across all pozi subdomains
      expires: 31, // in days
    })

    // console.debug('Model - initialise - set cookie', site)
  }

  lset(__GModelData, 'app.localhost', localhost)
  lset(__GModelData, 'app.site', site)

  console.debug('getSiteNameFromURL - sitename', site)

  if (mode === 'go') {
    console.log('Detected site go... get site config from redirector share url.')
    __GModelData.initialised = true
    return
  }

  const showSiteList = localhost && !site
  if (showSiteList) {
    lset(__GModelData, 'app.state.sitelist[0]', true)
    __GModelData.initialised = true
    return
  }

  setStateFromUrl()

  console.debug('Model - initialise - getSiteConfig', site, map)
  console.debug('Current environment: ', process.env.NODE_ENV)

  // If the map parameter is provided, the site config is to be loaded using the internal API
  if (map) {
    // API errors are handled by the query client, which will update Redux state as required
    const config = await getSiteConfigNew(site, map)
    setSiteConfig(config)
    return
  }

  // If the map parameter is not provided, the legacy site config is to be loaded from the S3 bucket
  try {
    const config = await getSiteConfig(site)
    setSiteConfig(config)
  } catch (jqxhr) {
    // getSiteConfig uses jQuery Ajax - error is a jQuery XHR object
    console.error('jQuery Ajax error from getSiteConfig:', jqxhr.status, jqxhr.responseText)
    onSiteConfigError(site, '', jqxhr)
  }
}

export const getFeatureValuesFromState = (featureField) => {
  const featuresFromState = getStateArray('feature', [])

  // Get all values for the fields stored in the state
  const values = featuresFromState.filter((feature) => feature[featureField]).map((feature) => feature[featureField])

  return values
}

/**
 * Update the model app.state from the old location.search url
 * ?site=wcma&lon=141.73428&lat=-36.85323&z=9&click=142.28085,-36.45219&layers=wetlands
 */
export const setStateFromSearchParams = function () {
  if (window.location && window.location.search && window.location.search.trim() === '') {
    console.debug('setStateFromSearchParams - no window.location.search params found.')
    return {}
  }

  const searchURL = window.location.search.substring(1).trim() || ''
  if (searchURL === '') {
    console.debug('setStateFromSearchParams - no search params found to convert.')
    return {}
  }

  console.debug('setStateFromSearchParams - searchURL', searchURL)

  setStateFromSearchURL('z', searchURL)
  setStateFromSearchURL('target', searchURL)
  setStateFromSearchURL('lgacode', searchURL)
  setStateFromSearchURL('filter', searchURL)
  setStateFromSearchURL('filterlock', searchURL)

  // E.g. http://127.0.0.1:3000/?site=cardinia&propnum=2342
  // Translates to: http://127.0.0.1:3000/#/site[cardinia]/search[propnum,2342]/

  // http://localhost:3000/?site=loddon-new&propnum=67717600,67717700,67717750,67717800

  // TODO: Get these dymamically from layer.search.id
  const searchIds = ['propnum', 'propertypfi', 'parcelpfi', 'parcelspi']

  for (const key in searchIds) {
    const searchId = searchIds[key]
    const value = PoziUtils.getUrlParameter(searchId, searchURL)
    if (value && value !== '') {
      setState('search', searchId, value)
    }
  }

  const lon = PoziUtils.getUrlParameter('lon', searchURL)
  if (lon && lon !== '') {
    setState('x', null, lon)
  }

  const lat = PoziUtils.getUrlParameter('lat', searchURL)
  if (lat && lat !== '') {
    setState('y', null, lat)
  }

  const hideDefaultLayers = PoziUtils.getUrlParameter('hidedefaultlayers', searchURL)
  if (hideDefaultLayers && hideDefaultLayers !== '') {
    setState('hidedefaultlayers', null, true)
  }

  const layersStr = PoziUtils.getUrlParameter('layers', searchURL)
  if (layersStr) {
    unset('app.state.layers')
    const layersArr = layersStr.split(',')
    for (const lkey in layersArr) {
      let layerid = layersArr[lkey]
      layerid = PoziUtils.getPoziIDFromString(layerid)
      if (isNil(layerid)) {
        return
      }
      setState('layers', null, layerid) // Unset done above array generation.
    }
  }

  // e.g. http://localhost:3000/?site=southerngrampians&feature=publictoilets:14978
  const feature = PoziUtils.getUrlParameter('feature', searchURL)
  if (feature) {
    const featureArr = feature.split(':')
    if (featureArr.length === 2) {
      const [layerTitleId, featureId] = featureArr
      setState('feature', layerTitleId, featureId)

      // also enable corresponding layer in case it was not provided in URL
      setState('layers', null, layerTitleId)
    }
  }
}

export const setStateFromSearchURL = function (urlparam, searchURL) {
  const value = PoziUtils.getUrlParameter(urlparam, searchURL)
  if (value !== '') {
    setState(urlparam, null, value)
  }
}

/**
 * Need a way to avoid issue with values changing.
 * I think this could be done better, E.g. Remember old value?
 * E.g. x,y,z are different values each time they are set, so how to know to override these?
 * But we do not want to overide layers, fieldnames, etc.
 * Try to use plural terms for these names, e.g. Layers, Fieldnames, etc.
 * And try to use singular nouns for other names. E.g. click, target, etc.
 */
export const isMultiParent = function (parent) {
  let bMultiParent = false
  switch (parent) {
    case 'layers':
    case 'feature':
    case 'fieldnames':
    case 'layergroup':
      bMultiParent = true
      break
    default:
    // console.log('Not multi parent.', parent)
  }
  return bMultiParent
}

/**
 * Set the state of a single variable.
 */
export const setState = function (parent, key, value) {
  // console.debug('setState', parent, key, value)

  if (isNil(parent)) {
    console.error('Must pass a parent name to setState.')
    return
  }

  if (isNil(value)) {
    console.error('Must pass a value to setState! Use unset if you want to remove a parent or key.', parent, key, value)
    return
  }

  parent = String(parent) // Cast to string

  if (!isBoolean(value)) {
    value = String(value) // Cast to string
  }

  let obj = {}
  let stateArr = []
  let stateArrFinal = []

  // If a state variable can have multiple values, check if it already exists
  if (isMultiParent(parent)) {
    stateArr = lget(__GModelData, 'app.state.' + parent, [])

    if (stateArr) {
      // Check if the new value already exists in the state variable
      const exists = stateArr.some((state) => {
        if (!key) {
          return state === value
        } else {
          return state[key] === value
        }
      })

      // If the value exists ignore it, otherwise keep the existing value(s) and add the new one
      if (exists) {
        return
      } else {
        stateArrFinal = stateArr
      }
    }
  }

  if (key) {
    obj = {}
    obj[key] = value
    stateArrFinal.push(obj)
  } else {
    stateArrFinal.push(value)
  }

  _set('app.state.' + parent, stateArrFinal)
}

/**
 * Find value in state array
 * @returns boolean
 */
export const valueInStateArray = function (parent, value) {
  const stateArr = get('app.state.' + parent, null)
  if (stateArr) {
    for (const key in stateArr) {
      if (stateArr[key] === value) {
        return true
      }
    }
  }
  return false
}

export const setStateFromUrl = () => {
  parseHashWidget()
  setStateFromHash()
  setStateFromSearchParams()
}

export const setStateFromHash = () => {
  const hashStr = window.location.hash
  const hashItems = parseHash(hashStr)

  const hashKeys = hashItems.map((item) => item.key)

  // console.debug('setStateFromHash - hash items', hashKeys, hashItems)

  // const hashObjItems = Object.entries(hashObj)
  // for (let [itemParent, values] of hashObjItems) {
  for (const hashItem of hashItems) {
    // console.debug('setStateFromHash - hashItem', hashItem)

    const { key: itemParent, values } = hashItem

    // console.debug('setStateFromHash', itemParent, values)

    // Explicitly update the app to reflect the updated hash
    switch (itemParent) {
      case 'feature': {
        // Extract titleid, idField, idValues from the hash
        // Handle the following cases:
        // * feature[assets, 1002] // only titleid is provided
        // * feature[assets, 1002, 1003] // only titleid is provided and multiple values
        // * feature[assets.asset_id, 1002] // both titleid and idField are provided
        // * feature[assets.asset_id, 1002, 1003] // both titleid and idField are provided with multiple values

        const [itemKey = '', ...itemValues] = values.map((value) => value.trim())

        // Store the feature(s) in the model
        for (const value of itemValues) {
          if (value) {
            setState('feature', itemKey, value)
          }
        }

        // Set auto zoom to feature or provided zoom level if X and Y are not provided
        const hashIncludesXY = hashKeys.includes('x') && hashKeys.includes('y')
        const hashZoomLevel = hashItems.find((item) => item.key === 'z')?.values?.[0]

        // console.debug('setStateFromHash - hashIncludesXY', hashIncludesXY, 'hashZoom', hashZoomLevel)

        let autoZoom = false
        if (!hashIncludesXY) {
          autoZoom = hashZoomLevel ?? true
        }

        // console.debug('setStateFromHash - autoZoom', autoZoom)

        if (autoZoom) {
          setState('autozoom', null, autoZoom)
        }

        // Make sure that the info tab is opened if the tab hash has not been provided.
        // This is to ensure that the link-ins are shown in the info tab upon load.
        const hashIncludesTab = hashKeys.includes('tab')
        if (!hashIncludesTab) {
          setState('tab', null, 'info')
        }

        break
      }

      case 'search': {
        const [itemKey = '', itemValue = ''] = values

        setState('search', itemKey, itemValue)

        break
      }

      // Store individual items one by one if they are a multiparent item
      case 'fieldnames':
      case 'layers':
      case 'layergroup': {
        for (const value of values) {
          if (value) {
            setState(itemParent, null, value)
          }
        }

        break
      }

      default: {
        // console.debug('setStateFromHash - default', itemParent, values)

        let itemKey
        let itemValue

        // For single values, we set the itemKey to null.
        // This allows for setting the state in a generic way in Model.setState()
        if (values.length === 1) {
          itemKey = null
          itemValue = values[0]
        } else {
          ;[itemKey, itemValue] = values
        }

        // We convert 'true'/'false' string values to boolean values.
        switch (String(itemValue).toLowerCase()) {
          case 'false':
            itemValue = false
            break
          case 'true':
            itemValue = true
            break
        }

        // console.debug('setStateFromHash - default - itemKey / itemValue', itemParent, itemKey, itemValue)

        setState(itemParent, itemKey, itemValue)
      }
    }
  }

  // One day we need to revisit this and incorporate it into this function
  // parseHashWidget()
}

// Parses this sort of thing
//   /widget.html#site=cardinia&redirect=true
// http://localhost:3000/widget.html#site=cardinia&redirect=true
export const parseHashWidget = function () {
  if (includes(window.location.hash, 'widget=true')) {
    const hashStr = decodeURIComponent(window.location.hash.substring(1).trim())
    const hashArr = hashStr.split('&')
    console.debug('parseHashWidget', hashArr)
    for (const hashKey in hashArr) {
      const keyValue = hashArr[hashKey]
      const keyValueArr = keyValue.split('=')
      if (keyValueArr.length === 2) {
        const key = keyValueArr[0]
        let value = keyValueArr[1]

        // Turn true/false values into booleans
        if (value === 'true') {
          value = true
        } else if (value === 'false') {
          value = false
        }

        // Any value with a comma, we'll split up, unless it's a `query` field (address for example)
        if (includes(value, ',') && key !== 'query') {
          const multiValueArr = value.split(',')
          for (const multiValueKey in multiValueArr) {
            value = multiValueArr[multiValueKey]
            console.debug('parseHashWidget - multiValue', hashKey, key, value)
            setState(key, null, value)
          }
        } else {
          console.debug('parseHashWidget - singleValue', hashKey, key, value)
          setState(key, null, value)
        }
      }
    }
  }
}

/**
 * Get the whole model object
 */
export const getAll = () => {
  return __GModelData
}

/**
 * Get the whole app object
 */
export const getAppAll = () => {
  return get('app', null)
}

/**
 * Get the first item of the state array for specified parent.
 */
export const getState = function (parent, defaultvalue) {
  return get('app.state.' + parent + '[0]', defaultvalue)
}

/**
 * Get the whole state object
 */
export const getStateAll = () => {
  return get('app.state', null)
}

/**
 * Get the entire state array for specified parent
 */
export const getStateArray = function (parent, defaultvalue) {
  return get('app.state.' + parent, defaultvalue)
}

export const getAppDefaults = async () => {
  // console.debug('getAppDefaults')

  const defaultsUrl = `https://${configLocation}/public/defaults.json`

  try {
    const defaults = await PoziAPIData().ajax({
      timeout: 1000 * 10, // 10 seconds timeout
      url: defaultsUrl
    })

    // console.debug('getAppDefaults - results', defaults)

    if (!defaults) {
      console.log('getAppDefaults - defaults file is empty', defaults)
      return
    }

    if (typeof defaults !== 'object' || defaults instanceof Array) {
      console.log('getAppDefaults - defaults file is not an object or is an array. Ignoring.', defaults)
      return
    }

    // Store in model (not redux for now)
    lset(__GModelData, 'app.defaults', defaults)
  } catch (xhr) {
    const { state, statusCode, statusText } = xhr
    console.log('getAppDefaults - error', statusCode(), state(), statusText, xhr)
  }
}

/**
 * Get siteconfig from server
 *
 * https://lodash.com/docs/4.17.4#get
 * @param {string} url a value siteconfig url
 * @returns {json} siteconfig
 *
 */
export const getSiteConfig = async function (sitename) {
  let url = ''

  const isPreview = lget(__GModelData, 'app.state.preview[0]', false)
  const configFolder = isPreview ? 'preview' : 'production'

  // Fetch and store default values first
  await getAppDefaults()

  // Direct link to AWS bucket (not advisable as files don't get compressed on the fly)
  // url = `https://s3.ap-southeast-2.amazonaws.com/config.pozi.com/public/${configFolder}/${sitename}.json`

  url = `https://${configLocation}/public/${configFolder}/${sitename}.json`

  // For debugging purposes (store in public/config/<sitename>.json):
  // url = './config/' + sitename + '.json'

  // Request site config
  return PoziAPIData().ajax({ url, cache: false })
}

const onSiteConfigError = (sitename, map, error) => {
  console.error('onSiteConfigError', sitename, map, error)

  let status, statusText, errorData, url

  // Handle different error types
  if (error.response) {
    // Axios error (from getSiteConfigNew)
    status = error.response.status
    statusText = error.response.statusText
    errorData = error.response.data
    url = error.config?.url || 'UNKNOWN URL'
    console.error('getSiteConfigNew - Axios error', status, statusText, errorData)
  } else {
    // jQuery Ajax error (from getSiteConfig)
    status = error.status
    statusText = error.statusText
    errorData = error.responseText
    url = 'UNKNOWN URL' // TODO: get URL from jQuery error
    console.error('getSiteConfig - jQuery Ajax error', status, statusText, errorData)
  }

  console.log('getSiteConfig - network connection status', sitename, navigator.connection)

  let messagePrimary, messageSecondary

  // First check if we're actually online
  if (!navigator.onLine) {
    messagePrimary = 'Your device appears to be offline'
    messageSecondary = 'Please check your connection and try again'
  } else if (status === 404) {
    if (map) {
      messagePrimary = `Map '${map}' for site '${sitename}' cannot be found`
    } else {
      messagePrimary = `Site '${sitename}' cannot be found`
    }
  } else if (status === 500) {
    if (map) {
      messagePrimary = `Server error loading map '${map}' for site '${sitename}'`
    } else {
      messagePrimary = `Server error loading site '${sitename}'`
    }
  } else {
    const messages = ['Unexpected error']

    if (status) {
      messages.push(status)
    }

    if (statusText && statusText !== 'error') {
      messages.push(statusText)
    }

    // Add error data if available and meaningful
    if (errorData && typeof errorData === 'object' && errorData.err_desc) {
      messages.push(errorData.err_desc)
    }

    messagePrimary = messages.join(' - ')

    const subject = encodeURIComponent(messagePrimary)
    const body = encodeURIComponent(url)

    messageSecondary = `
      Check your network connection and refresh the page. If the problem persists, please
      <a href="mailto:support@pozi.com?subject=${subject}&body=${body}">report this error</a>.`
  }

  siteNotFound(messagePrimary, messageSecondary)
}

export const setSiteConfig = (rawSiteConf) => {
  console.debug('setSiteConfig - config', rawSiteConf)

  // Store raw site config in model
  lset(__GModelData, 'rawconfig', rawSiteConf)

  // Sanity check
  if (!__GModelData.rawconfig) {
    throw new Error('getSiteConfig - Invalid config file')
  }

  // Create a deep copy (i.e. clone) of the rawSiteConf
  let siteConf = cloneDeep(rawSiteConf)

  // Run migration scripts
  siteConf = Migrate(siteConf)

  // Store siteConf in model
  lset(__GModelData, 'config', siteConf)

  // Set some layer defaults. This includes adding special layers like highlight,
  // annotation and whatshere.
  setLayerDefaults()

  // Create a deep copy (i.e. clone) of siteConf
  const siteConfCopy = cloneDeep(siteConf)

  // Store copy in Redux
  window.store.dispatch(setConfig(siteConfCopy))

  // Store copy in Pozi Model as well
  lset(__GModelData, 'configcopy', siteConfCopy)

  // This is for the Config Manager (i.e. Admin interface)
  const bLoadConfig = lget(__GModelData, 'app.state.config[0]', false)
  if (bLoadConfig) {
    window.store.dispatch(configManagerOpen())
  }

  __GModelData.initialised = true
}

export const siteNotFound = function (messagePrimary = '', messageSecondary = '') {
  console.log('PoziConfig: Unable to load site, redirect to Pozi website.')

  if (messagePrimary) {
    $('#site-config-load-error-message-primary').html(messagePrimary)
  }

  if (messageSecondary) {
    $('#site-config-load-error-message-secondary').html(messageSecondary)
  }

  $('body').html($('#site-config-load-error').removeClass('hidden'))

  document.title = 'Pozi Web Map'
}

/**
 * Get some data from the model. Based on Lodash get module.
 *
 * https://lodash.com/docs/4.17.4#get
 * @param {string} path
 * @param {any} defaultvalue
 * @returns {any}
 *
 */

export const get = function (path, defaultvalue = undefined) {
  if (!__GModelData.initialised) {
    console.error(
      '!!! ERROR !!! Model get called before initialise. Debug info: Model path requested: ',
      path,
      cloneDeep(__GModelData)
    )
    return null
  }
  // console.log("Model get", path, defaultvalue)

  const bDefaultValueDefined = typeof defaultvalue !== 'undefined'
  if (bDefaultValueDefined) {
    // console.log("Default value", path, defaultvalue)
  } else {
    // console.log("No default value defined, return null if value not found.")
    defaultvalue = null
  }

  const value = lget(__GModelData, path, undefined)
  if (typeof value === 'undefined') {
    if (bDefaultValueDefined) {
      // console.log('Model path ' + path + ' not found returning default value', defaultvalue)
      return defaultvalue
    } else {
      console.log(
        'Model path not found and no default return value specified. If you have not initialised the variable, then pass a default value.',
        path
      )
      debugconsole() // This should not occur in normal operation, so output the model.
    }
  }
  // console.log('Model get', path, value)
  return value
}

/**
 * Query the site config.
 *
 * https://lodash.com/docs/4.17.4#find
 * @param {string} path Some path to the data. See Lodash get module for details
 * @param {object} findobj Some object to find in path
 * @param {object} default Some default value to return if path doesn't exist
 * @returns {object}
 *
 */

export const find = function (path, findobj, defaultvalue) {
  if (!__GModelData.initialised) {
    console.error('!!! ERROR !!! Model find called before initialise. Debug info: Model path requested: ', path)
    return null
  }
  // console.log("Model get", path, defaultvalue)

  const bDefaultValueDefined = typeof defaultvalue !== 'undefined'
  if (!bDefaultValueDefined) {
    defaultvalue = null
  }

  const value = lget(__GModelData, path, undefined)
  if (typeof value === 'undefined') {
    if (bDefaultValueDefined) {
      return defaultvalue
    } else {
      console.log(
        'Model path not found and no default return value specified. If you have not initialised the variable, then pass a default value.',
        path
      )
      debugconsole() // This should not occur in normal operation, so output the model.
    }
  } else {
    const foundObj = find(value, findobj)
    if (typeof foundObj === 'undefined') {
      return defaultvalue
    } else {
      return foundObj
    }
  }
  // console.log('Model get', path, value)
  return value
}

/**
 * Set site config value
 *
 * https://lodash.com/docs/4.17.4#set
 * @param {path} string Some path to the data. See Lodash get module for details.
 * @param {any} value Some object to set as the value
 * @returns {boolean} Success or Failure
 *
 */

export const set = function (path, value) {
  if (!__GModelData.initialised) {
    console.error('!!! ERROR !!! Model set called before initialise. Debug info: Model path requested: ', path)
    return false
  }

  // Do not set value if it is the same as the previous value
  // const prevValue = lget(__GModelData, path)
  // if (prevValue === value) {
  //   return false
  // }

  return _set(path, value)
}

/**
 * Set site config value and call observers.
 *
 * https://lodash.com/docs/4.17.4#set
 * @param {path} string
 * @param {object} value
 * @returns {boolean} Success or Failure
 *
 */
const _set = function (path, value) {
  // console.debug('Model _set', path, value)

  const prevValue = lget(__GModelData, path)

  lset(__GModelData, path, value)

  for (const key in __GModelObservers[path]) {
    const observer = __GModelObservers[path][key]
    const eventHandled = lget(__GModelData, path + '.handled', false)

    if (eventHandled) {
      break
    }

    if (typeof observer === 'function') {
      // console.debug('Model _set - observer called', path, key, observer.name, value)
      observer(value, prevValue)
    }
  }

  return true
}

/**
 * Unset site config path
 *
 * https://lodash.com/docs/4.17.4#unset
 * @param {string} path Lodash style path
 * @returns {boolean} Success or Failure
 *
 */

export const unset = function (path) {
  if (!__GModelData.initialised) {
    console.error('!!! ERROR !!! Model unset called before initialise. Debug info: Model path requested: ', path)
    return false
  }

  // const prevValue = lget(__GModelData, path)
  // if (prevValue === undefined) {
  //   return false
  // }

  return _unset(path)
}

/**
 * Unset site config path
 *
 * https://lodash.com/docs/4.17.4#unset
 * @param {string} path Lodash style path
 * @returns {boolean} Success or Failure
 *
 */
const _unset = function (path) {
  const prevValue = lget(__GModelData, path)

  // console.debug('Model _unset', path, prevValue)

  lunset(__GModelData, path)

  for (const key in __GModelObservers[path]) {
    const observer = __GModelObservers[path][key]

    if (typeof observer === 'function') {
      // console.debug('Model _unset - observer called', path, observer.name)
      observer(undefined, prevValue)
    }
  }

  return true
}

export const unsetByValue = function (path, value) {
  const arr = get(path, [])
  /* const removedElements = */ remove(arr, function (toRemove) {
    return toRemove === value
  })
  set(path, arr)
}

/**
 * Set a default value if it doesnt exist in the config.
 *
 * https://lodash.com/docs/4.17.4#set
 * @param {string} path Lodash style path
 * @param {object} value
 *
 */
export const setDefault = function (path, value) {
  const valueExists = lget(__GModelData, path, null) !== null
  if (!valueExists) {
    // console.log('Model path ' + path + ' setting default to', value)
    return lset(__GModelData, path, value) // Lodash function
  } else {
    // Default value already set.
  }
}

/**
 * Output the current site config for debugging.
 */
export const debugconsole = function () {
  console.log('>>>>> Model - state is valid at time object is expanded >>>>>')
  console.log('Object', __GModelData)
  console.log('<<<<< Model <<<<<')
  console.trace()
}

/**
 * Listen for a change to the model.
 * @param {string} path The path of the variable.
 * @param {function(?)} listener The listener function.
 * @return {number} Array index of the observer function on the path array.
 * @model
 */

export const on = function (path, fn) {
  if (!__GModelObservers[path] || !Array.isArray(__GModelObservers[path])) {
    __GModelObservers[path] = []
  }

  // Register new listener
  // Only allow a function to be subscribed to a path once
  const bIsNotRegisteredYet = !__GModelObservers[path].includes(fn)
  if (bIsNotRegisteredYet) {
    // console.debug('Model listener is new', path, fn)
    __GModelObservers[path].push(fn)
  } else {
    // console.debug('Observer is already registered! Did you mean to register multiple times?', path)
  }

  const observerIndex = __GModelObservers[path].indexOf(fn)

  if (observerIndex < 0) {
    console.error('Invalid observer count! Failed to register observer for path', observerIndex, path)
    return 0
  }
  // Sanity check if some loop code registers a huge number of listeners on a path.
  if (observerIndex >= 1000) {
    console.error('Invalid observer count. Too many observers for the path.', observerIndex, path)
    return 0
  }
  // console.log('Added observer to path', path, observercount)
  return observerIndex
}

/**
 * Unlisten for a change to the model.
 * @param {string} path The path of the variable.
 * @param {number} observerindex index of the observer function on the path array.
 * @return {bool} Succesfully unregistered the listener.
 * @model
 */

export const un = function (path, observerindex) {
  if (path !== '' && observerindex >= 0 && __GModelObservers[path] && __GModelObservers[path][observerindex]) {
    __GModelObservers[path][observerindex] = null
    return true
  } else {
    const err = 'Cannot remove an invalid observerindex! Path: ' + path + ' observerindex: ' + observerindex
    console.error(err)
  }
  return false
}

/**
 * setLayerDefaults - Does what it says on the tin
 *
 * It loops through all current layers in config.layers and sets defaults. It
 * also adds a highlight, annotation and whatshere layer at the top
 *
 */
export const setLayerDefaults = function () {
  AddDefaultExcludes(__GModelData)

  // Create machine readable IDs for title and group
  let layers = lget(__GModelData, 'config.layers', [])
  addAnnotationLayer(layers)
  addWhatsHereLayer(layers)
  addCurrentTargetLayer(layers)

  for (const layer of layers) {
    // Generate titleid from layer title
    layer.titleid = PoziUtils.getPoziIDFromString(layer.title)

    // console.debug('model - layers', layer.titleid)

    const showInLayerControlExists = lget(layer, 'showInLayerControl', null)
    if (isNil(showInLayerControlExists)) {
      layer.showInLayerControl = true
    } else {
      layer.showInLayerControlRawConfig = lget(layer, 'showInLayerControl', true)
    }

    // Allow for empty group name and generate group ID programmatically if not empty
    // Empty group names will result in layers being in their own group
    if (!layer.group) {
      layer.group = ''
      layer.groupid = ''
    } else {
      layer.groupid = PoziUtils.getPoziIDFromString(layer.group)
    }

    // The following block may not be needed any more: if a spatial id has
    // not been provided, we will default to $id anyway
    const bLayerHasSpatial = has(layer, 'config.spatial')
    const bLayerMissingSpatialId = !has(layer, 'config.spatial.id')
    const idValue = lget(layer, 'config.spatial.id', '')
    const bLayerIdValueEmpty = idValue === ''
    if (bLayerHasSpatial) {
      if (bLayerMissingSpatialId || bLayerIdValueEmpty) {
        lset(layer, 'config.spatial.id', DEFAULT_ID_WHEN_UNSET)
      }
    }
  }

  // Remove any layers that do not have a titleid
  layers = layers.filter((layer) => !!layer.titleid)

  // Store updated layers in config.layers
  lset(__GModelData, 'config.layers', layers)
}

export const addAnnotationLayer = function (layers) {
  const bEditEnabled = lget(__GModelData, 'config.layout.drawer.tabs.tools.annotations.enabled', true)
  if (!bEditEnabled) {
    return
  }

  const bAnnotationsInState = lget(__GModelData, 'app.state.annotations', null)
  let bShowInLayerControl = false
  if (isArray(bAnnotationsInState)) {
    bShowInLayerControl = true
  }

  const annotationLayer = {
    title: ANNOTATION_LAYER_TITLEID,
    titleid: ANNOTATION_LAYER_TITLEID,
    group: 'PoziGeneral',
    type: 'GeoJSON',
    visible: true,
    queryable: false,
    showInLayerControl: bShowInLayerControl,
    edit: true,
    config: {
      spatial: {
        url: '',
        id: 'id',
        label: 'label',
        loader: 'annotation' // special loader for this layer
      }
    }
  }
  layers.unshift(annotationLayer) // Add to top of layers to ensure it is on top of all other layers.
}

export const addCurrentTargetLayer = function (layers) {
  layers.unshift(currentTargetLayerConfig) // Add to top of layers to ensure it is on top of all other layers.
}

export const addWhatsHereLayer = function (layers) {
  layers.unshift(whatsHereLayerConfig) // Add to top of layers to ensure it is on top of all other layers.
}
